import { useEffect } from 'react';

type BreadcrumbItem = {
  name: string;
  url: string;
};

export type FaqItem = {
  question: string;
  answer: string;
};

export type SeoHeadProps = {
  title: string;
  description: string;
  canonicalUrl: string;
  pagePath: string;
  faqItems?: FaqItem[];
  breadcrumbItems?: BreadcrumbItem[];
};

const upsertMetaTag = (
  selector: string,
  attributes: Record<string, string>,
  content: string
) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const upsertCanonical = (canonicalUrl: string) => {
  let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', canonicalUrl);
};

const buildFaqSchema = (faqItems: FaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

const buildBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

const buildWebPageSchema = (
  title: string,
  description: string,
  canonicalUrl: string,
  pagePath: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: title,
  description,
  url: canonicalUrl,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Workflow.Estate',
    url: 'https://workflow.estate',
  },
  about: [
    'Real estate CRM',
    'Agency workflow management',
    'Open home management',
    'Lead tracking',
    'Buyer follow-up',
  ],
  breadcrumb: {
    '@id': `https://workflow.estate${pagePath}#breadcrumb`,
  },
});

const upsertJsonLd = (id: string, schema: object) => {
  let script = document.getElementById(id) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }

  script.text = JSON.stringify(schema);
};

const removeJsonLd = (id: string) => {
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }
};

const SeoHead = ({
  title,
  description,
  canonicalUrl,
  pagePath,
  faqItems = [],
  breadcrumbItems = [],
}: SeoHeadProps) => {
  useEffect(() => {
    const pathKey = pagePath.replace(/\//g, '-');

    document.title = title;

    upsertMetaTag('meta[name="description"]', { name: 'description' }, description);
    upsertMetaTag('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMetaTag('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMetaTag('meta[property="og:type"]', { property: 'og:type' }, 'website');
    upsertMetaTag('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    upsertMetaTag('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    upsertMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    upsertMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    upsertCanonical(canonicalUrl);

    upsertJsonLd(
      `seo-webpage${pathKey}`,
      buildWebPageSchema(title, description, canonicalUrl, pagePath)
    );

    if (breadcrumbItems.length > 0) {
      upsertJsonLd(
        `seo-breadcrumb${pathKey}`,
        buildBreadcrumbSchema(breadcrumbItems)
      );
    } else {
      removeJsonLd(`seo-breadcrumb${pathKey}`);
    }

    if (faqItems.length > 0) {
      upsertJsonLd(`seo-faq${pathKey}`, buildFaqSchema(faqItems));
    } else {
      removeJsonLd(`seo-faq${pathKey}`);
    }
  }, [title, description, canonicalUrl, pagePath, faqItems, breadcrumbItems]);

  return null;
};

export default SeoHead;
